// apps/client/src/hooks/pipelineTracker/useComplianceReport.ts
//
// Pipeline Tracker: broker file-quality metrics.
//
// BROKER QUALITY ONLY. Every figure comes from what the broker did, never from
// the customer's financial position. The Compliance export carries DTI, HEM
// variance, net asset and cash position and monthly expenses; none of them has
// a warehouse column, so none of them can appear here.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { REPORT_CALLABLE, COMPLIANCE_REPORT_ACTION } from './reportContract';

export interface ComplianceBrokerRow {
  adviserId: string;
  adviserName: string;
  deals: number;
  avgProductsCompared: number | null;
  productsComparedMissing: number;
  medianWordCount: number | null;
  wordCountMissing: number;
  tmdComplete: number;
  tmdMissing: number;
  medianInterestRateBp: number | null;
  interestRateCoverage: number;
  medianTotalLvrBp: number | null;
  totalLvrCoverage: number;
}

export interface ComplianceReportData {
  asAtDate: string;
  totalDeals: number;
  company: ComplianceBrokerRow;
  brokers: ComplianceBrokerRow[];
}

export type ComplianceResult =
  | { status: 'ok'; data: ComplianceReportData }
  | { status: 'no_snapshot' };

export function useComplianceReport(companyId: string | undefined, asAtDate?: string) {
  const [result, setResult] = useState<ComplianceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, REPORT_CALLABLE);
      // The key is OMITTED when there is no date, never sent as undefined.
      // Firebase serialises undefined to null over the wire and the server's
      // zod .optional() rejects null, so passing it through fails validation
      // before any query runs and the tab reports a load failure.
      const res = await call({ action: COMPLIANCE_REPORT_ACTION, companyId, ...(asAtDate ? { asAtDate } : {}) });
      setResult(res.data as ComplianceResult);
    } catch (err) {
      console.error('[PipelineTracker] complianceReport failed', err);
      setError('The compliance report could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [companyId, asAtDate]);

  useEffect(() => { void load(); }, [load]);
  return { result, loading, error, reload: load };
}
